import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const CurrencyEditModal = ({ currency, setCurrency, onEditSuccess }) => {
  const [exchangeRate, setExchangeRate] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Populate form state when currency is selected for editing
  useEffect(() => {
    if (currency) {
      setExchangeRate(currency.exchangeRate?.toString() || "");
      setEnabled(currency.enabled ?? true);
      setErrorMsg("");
    }
  }, [currency]);

  const handleClose = () => {
    setCurrency(null);
    setErrorMsg("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!currency?.code) return;

    const rate = parseFloat(exchangeRate);
    if (isNaN(rate) || rate <= 0) {
      setErrorMsg("❌ Rate to GBP must be a number greater than 0.");
      return;
    }

    try {
      setSaving(true);
      setErrorMsg("");

      const docRef = doc(db, "currencies", currency.code);
      await updateDoc(docRef, {
        exchangeRate: rate,
        enabled: enabled,
        updatedAt: serverTimestamp(),
      });

      if (onEditSuccess) {
        onEditSuccess();
      }
      handleClose();
    } catch (error) {
      console.error("Error updating currency:", error);
      setErrorMsg("❌ Failed to update currency. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={!!currency} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Currency</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body>
          {errorMsg && (
            <div className="alert alert-danger py-2 rounded-3 small" role="alert">
              {errorMsg}
            </div>
          )}

          {/* Currency Code (Read-Only) */}
          <Form.Group className="mb-3" controlId="editCurrencyCode">
            <Form.Label className="fw-semibold">Currency Code</Form.Label>
            <Form.Control
              type="text"
              value={currency?.code || ""}
              disabled
              readOnly
            />
            <Form.Text className="text-muted">
              Currency code and document ID cannot be edited.
            </Form.Text>
          </Form.Group>

          {/* Rate to GBP */}
          <Form.Group className="mb-3" controlId="editExchangeRate">
            <Form.Label className="fw-semibold">Rate to GBP</Form.Label>
            <Form.Control
              type="number"
              step="0.0001"
              min="0.0001"
              value={exchangeRate}
              onChange={(e) => setExchangeRate(e.target.value)}
              required
              disabled={saving}
              placeholder="e.g. 1.25"
            />
          </Form.Group>

          {/* Enabled Status Switch */}
          <Form.Group className="mb-3" controlId="editEnabledSwitch">
            <Form.Label className="fw-semibold d-block">Status</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  style={{
                    width: "2.5em",
                    height: "1.25em",
                    cursor: "pointer"
                  }}
                  disabled={saving}
                />
              </div>
              <span className={`fw-semibold ${enabled ? "text-success" : "text-danger"}`}>
                {enabled ? "Enabled" : "Disabled"}
              </span>
            </div>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? (
              <>
                <span
                  className="admin-loader-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Updating...
              </>
            ) : (
              "Update"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CurrencyEditModal;
